'use client';

import { useCallback, useState } from 'react';

import {
  clearBiometricCredentials,
  isBiometricAvailable,
  storeBiometricCredentials,
} from './native-biometric-service';
import { useNativeDetect } from './use-native-detect';
import {
  BIOMETRIC_ENROLLED_KEY,
  clearBiometricEnrolled,
  markBiometricEnrolled,
} from './use-biometric-token-sync';

function isBiometricEnrolled(): boolean {
  try {
    return typeof window !== 'undefined' && localStorage.getItem(BIOMETRIC_ENROLLED_KEY) === 'true';
  } catch {
    return false;
  }
}

/**
 * Hook for managing biometric auth state in native apps.
 * Uses a localStorage flag to check enrollment — never calls
 * isCredentialsSaved which triggers Face ID on iOS.
 */
export function useBiometricAuth() {
  const { isNative } = useNativeDetect();
  const [hasStoredCredentials, setHasStoredCredentials] = useState(
    isNative && isBiometricEnrolled(),
  );

  const storeCredentials = useCallback(
    async (userId: string, refreshToken: string) => {
      if (!isNative) return;

      const available = await isBiometricAvailable();
      if (!available) return;

      const stored = await storeBiometricCredentials(userId, refreshToken);
      if (stored) {
        markBiometricEnrolled();
        setHasStoredCredentials(true);
      }
    },
    [isNative],
  );

  const clearCredentials = useCallback(async () => {
    if (!isNative) return;
    await clearBiometricCredentials();
    clearBiometricEnrolled();
    setHasStoredCredentials(false);
  }, [isNative]);

  return {
    available: isNative,
    hasStoredCredentials,
    checked: true,
    storeCredentials,
    clearCredentials,
  };
}
